// Libraries & Node Modules
import algoliasearch from "algoliasearch/lite";
import { useState, useContext, useEffect } from "react";
import { renderToString } from "react-dom/server";
import { NextSeo } from "next-seo";
import { useSpecials } from "@/context/specials";
import { getSiteSettings } from '@/lib/getSiteSettings';
import { getShopCategoryMeta } from "@/lib/shopCategoryMeta";

// Algolia
import {
  InstantSearch,
  Hits,
  Configure,
  InstantSearchSSRProvider,
} from "react-instantsearch-hooks-web";
import { getServerState } from "react-instantsearch-hooks-server";

// Hooks & Context
import { useNextRouterHandler, decodeParam } from "@/hooks/useNextRouterHandler";
import StoreContext from "@/context/store";

// Components
import ProductCard from "@/components/product/ProductCard";
import Header from "@/components/shop/Header";
import Pagination from "@/components/shop/Pagination";
import CurrentRefinements from "@/components/shop/CurrentRefinements";
import ShopNav from "@/components/shop/ShopNav";
import NoResults from "@/components/shop/NoResults";
import Stats from "@/components/shop/Stats";

// Styles
import styles from "./shop.module.scss";
import productCardStyles from "@/components/product/ProductCard.module.scss";

const searchClient = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_KEY
);
const indexName = "menu-products-by-rating-production";

export async function getServerSideProps({ req, params }) {
  const siteSettings = await getSiteSettings();
  const protocol = req.headers.referer?.split("://")[0] || "https";
  const url = `${protocol}://${req.headers.host}${req.url}`;
  const initialStore = req.cookies.JANE_STORE || 4398;
  // Decode the category to handle URL encoding and normalize it to lowercase
  const category = decodeURIComponent(params.category).toLowerCase();

   // ✅ GET VALID ROOT TYPES FOR THIS STORE
   const index = searchClient.initIndex(indexName);
   const searchResult = await index.search('', {
     facets: ['root_types'],
     facetFilters: [`store_id:${initialStore}`]
   });
 
   const rootTypes = searchResult.facets?.root_types || {};
   const validCategories = Object.keys(rootTypes).map((key) => key.toLowerCase());
 
   // 🚫 RETURN 404 IF CATEGORY IS NOT VALID
   if (!validCategories.includes(category)) {
     return { notFound: true };
   }

  const serverState = await getServerState(
    <CategoryShopPage
      url={url}
      initialStore={initialStore}
      category={category}
    />,
    { renderToString }
  );

  return {
    props: {
      siteSettings,
      serverState,
      url,
      initialStore,
      category,
    },
  };
}

export default function CategoryShopPage({ serverState, url, initialStore, category }) {
  const { initialUiState, NextRouterHandler } = useNextRouterHandler({
    url,
    dynamicRouteQuery: { category },
    routeToState(params) {

      return {
        [indexName]: {
          query: params.q ? params.q : null,
          refinementList: {
            root_types: decodeParam(params.subcategory, "root_types"),
            brand: decodeParam(params.brand, "brand"),
            category: decodeParam(params.strain, "category"),
            feelings: decodeParam(params.effect, "feelings"),
            available_weights: decodeParam(params.weight, "available_weights"),
            applicable_special_ids: decodeParam(params.special, "applicable_special_ids"),
          },
          range: {
            bucket_price: params.price ? params.price : null,
            percent_thc: params.thc ? params.thc : null,
            percent_cbd: params.cbd ? params.cbd : null,
          },
          page: params.page ? params.page : null,
          sortBy: params.order ? params.order : null,
        },
      };
    },

    stateToRoute(uiState) {
      const indexUiState = uiState[indexName];

      const serializeParam = (param, key) =>
        param
          ?.map((item) =>
            key === "brand" ? item : item.toLowerCase().replace(/ /g, "+") // **Skip transformation for brand**
          )
          .join(",");

      return {
        subcategory: serializeParam(indexUiState.refinementList?.root_types),
        brand: serializeParam(indexUiState.refinementList?.brand, "brand"), // **Pass "brand" to prevent encoding**
        strain: serializeParam(indexUiState.refinementList?.category),
        effect: serializeParam(indexUiState.refinementList?.feelings),
        weight: serializeParam(indexUiState.refinementList?.available_weights),
        special: serializeParam(
          indexUiState.refinementList?.applicable_special_ids
        ),
        price: indexUiState.range?.bucket_price,
        thc: indexUiState.range?.percent_thc,
        cbd: indexUiState.range?.percent_cbd,
        q: indexUiState.query,
        page: indexUiState.page,
        order: indexUiState.sortBy,
      };
    },
  });

  const { store } = useContext(StoreContext);
  const [list, setList] = useState(false);
  const { specials, loading, error, fetchAllSpecials } = useSpecials();
  // const title = category.charAt(0).toUpperCase() + category.slice(1);
  const { title, description } = getShopCategoryMeta(category);

  // fetch product specials
  useEffect(() => {
    if (store) {
      fetchAllSpecials(store);
    }
  }, [store, fetchAllSpecials]);

  // repalce head w next seo
  return (
    <>
       <NextSeo
        title={title}
        description={description}
        openGraph={{
          title,
          description,
        }}
      />

      <main className="container container__slim">
        <InstantSearchSSRProvider {...serverState}>
          <InstantSearch
            searchClient={searchClient}
            indexName={indexName}
            initialUiState={initialUiState}
          >
            <NextRouterHandler />
            <Configure
              filters={`store_id:${store || initialStore} AND root_types:"${category}"`}
            />
            <header className={styles.header}>
              <Header
                url={`/shop/${category}`}
                category={category}
              />
            </header>


            <ShopNav url={url} list={list} setList={setList} />


            <div className={styles.stats}>
              <Stats />
              <CurrentRefinements productSpecials={specials} />
            </div>

            <div className={`${list ? productCardStyles.list : 'shop-product-grid'}`}>
              <NoResults >
                <Hits hitComponent={ProductCard} />
                <Pagination />
              </NoResults>
            </div>

          </InstantSearch>
        </InstantSearchSSRProvider>
      </main>
    </>
  );
}